import React from 'react';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import NavBar from './components/NavBar';
import { GiStopSign } from 'react-icons/gi';
import { FaTerminal, FaCogs, FaDownload, FaShieldAlt, FaFileCode, FaLinux } from 'react-icons/fa';
import ModDownloader from './components/ModDownloader';

const SectionHeader = ({ icon, title }) => (
  <div style={{
    background: 'rgba(15, 23, 42, 0.95)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  }}>
    {icon}
    <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>{title}</h2>
  </div>
);

const CodeBlock = ({ code }) => (
  <pre style={{
    background: 'rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#22c55e',
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    overflowX: 'auto',
    margin: '8px 0',
  }}>
    {code}
  </pre>
);

const ConfigRow = ({ param, description }) => (
  <Row style={{
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    padding: '12px 0',
  }}>
    <Col md={3} xs={4}>
      <code style={{ color: '#fbbf24', fontSize: '0.9rem' }}>{param}</code>
    </Col>
    <Col md={9} xs={8}>
      <span style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>{description}</span>
    </Col>
  </Row>
);

function ServerSetup() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, rgba(30, 41, 59, 0.95), rgba(30, 41, 59, 0.98))',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
    }}>
      <NavBar />
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card style={{
              background: 'rgba(30, 41, 59, 0.7)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              overflow: 'hidden',
            }}>
              <div style={{
                background: 'rgba(15, 23, 42, 0.95)',
                padding: '20px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                <h1 style={{ color: '#f1f5f9', margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>
                  O:HD Server Setup Instructions
                </h1>
              </div>

              <CardBody style={{ padding: '20px', color: '#cbd5e1' }}>
                <p style={{ marginBottom: '20px' }}>
                  The official O:HD server setup documentation you can find here:{' '}
                  <a
                    style={{ color: '#60a5fa' }}
                    href="https://docs.google.com/document/d/e/2PACX-1vQqN34apQWMGoqDuEdJnshnxOuguY5oSNKcLvHvIQKIM_m1FYe1kzqdQNAYhkuFzCfPSfINmdCu9aCs/pub"
                    target="_new"
                  >
                    Official O:HD Server setup guide
                  </a>
                </p>

                <SectionHeader icon={<FaTerminal color="#fbbf24" />} title="Startup Parameters" />
                <Card style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '20px' }}>
                  <CardBody style={{ padding: '16px' }}>
                    <ConfigRow param="BluforFaction" description="Set blufor faction" />
                    <ConfigRow param="OpforFaction" description="Set opfor faction." />
                    <ConfigRow param="MaxPlayers" description="Set max number of servers slots." />
                    <ConfigRow param="MinPlayers" description="Set minimum number of players (use together with BotAutofill)." />
                    <ConfigRow param="BluforNumBots" description="Set number of Blufor bots." />
                    <ConfigRow param="OpforNumBots" description="Set number of Opfor bots." />
                    <ConfigRow param="BluforNumTickets" description="Set number of tickets for BluFor faction." />
                    <ConfigRow param="OpforNumTickets" description="Set number of tickets for OpFor faction." />
                    <ConfigRow param="BotAutofill" description="Will fill up the server until set MinPlayers." />
                    <ConfigRow param="DisableKitRestrictions" description="Will disable any gear restrictions." />
                    <ConfigRow param="AutoAssignHuman" description="Will force player to a team. 0 to OpFor, 1 for BluFor. (COOP)" />
                    <ConfigRow param="Password" description="Set a password for the server in a quoted string." />
                    <ConfigRow param="-log" description="Enables logging." />
                    <ConfigRow param="-MapCycle" description="Enables use of MapCyle file." />
                    <ConfigRow param="-Port" description="Game Port (UDP) on which O:HD is bound." />
                    <ConfigRow param="-QueryPort" description="Port (UDP) for steam server queries." />
                    <ConfigRow param="-SteamServerName" description="Sets the name of the O:HD server." />
                    <ConfigRow param="-Whitelist" description="Lists steam64 ids in referenced file." />
                    <ConfigRow param="-AdminList" description="Lists steam64 ids with admin permissions." />
                    <ConfigRow param="-BanList" description="Lists steam64 ids denied access." />
                    <ConfigRow param="-SupportersOnly" description="Only O:HD supporters are allowed access." />
                  </CardBody>
                </Card>

                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ color: '#f1f5f9', marginBottom: '12px' }}>Example startup string:</h4>
                  <CodeBlock code={`HarshDoorstopServer-Linux-Shipping HarshDoorstop AlDhakir?BluforFaction=BP_HDFactionInfo_UnitedStates?OpforFaction=BP_HDFactionInfo_Insurgents?MaxPlayers=48?MinPlayers=24?BotAutofill?Password="ohd" -log -MapCycle -QueryPort=27005 -Port=7777 -SteamServerName="O:HD Server" -Whitelist=Whitelist.cfg`} />
                </div>

                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <GiStopSign color="#ef4444" size="40px" />
                  <span style={{ color: '#fca5a5' }}>
                    Before you go any further you should really start up the OHD server just once. This will create the framework of dirs and files to start with.
                  </span>
                </div>

                <SectionHeader icon={<FaTerminal color="#fbbf24" />} title="Rcon" />
                <Card style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '20px' }}>
                  <CardBody style={{ padding: '16px', color: '#cbd5e1' }}>
                    <p>In order to use Rcon you need to edit the Game.ini and provide some additional options.</p>
                    <p>Currently known RCON commands:</p>
                    <CodeBlock code="servertravel [mapname]" />
                    <CodeBlock code="servertravel AlDhakir?BluforFaction=BP_HDFactionInfo_TG_BluFor?OpforFaction=BP_HDFactionInfo_TG_OpFor?AutoAssignHuman=0" />
                    <p>
                      The afocommunity created a nice servertravel string generator.{' '}
                      <a style={{ color: '#60a5fa' }} href="https://hub.afocommunity.com/OHDTools/QueryGenerator" target="_new">
                        Query Generator
                      </a>
                    </p>
                  </CardBody>
                </Card>

                <SectionHeader icon={<FaDownload color="#fbbf24" />} title="Mods" />
                <Card style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '20px' }}>
                  <CardBody style={{ padding: '16px', color: '#cbd5e1' }}>
                    <p>Mod directories go in: <code style={{ color: '#fbbf24' }}>OHD_Server_PT_Linux/HarshDoorstop/Mods</code></p>
                    <p>To download mods through steamcmd:</p>
                    <CodeBlock code="steamcmd.sh +login anonymous +workshop_download_item 736590 [mod-file-id-here] validate +quit" />
                    <p>Move the mod directory from download structure to HarshDoorstop directory.</p>
                    <ModDownloader />
                    <p style={{ marginTop: '12px' }}>
                      For Windows, <b>LeeroyMajors#1026</b> has a batch file to manage mods:{' '}
                      <a style={{ color: '#60a5fa' }} href="https://ohdservers.net/contrib/OHD_Server_Update_Workshop_Start.txt" target="_new">
                        OHD_Server_Update_Workshop_Start
                      </a>
                    </p>
                  </CardBody>
                </Card>

                <SectionHeader icon={<FaCogs color="#fbbf24" />} title="Game Server Configuration" />
                <Card style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '20px' }}>
                  <CardBody style={{ padding: '16px', color: '#cbd5e1' }}>
                    <p>If the files already contain something you should append, not override.</p>
                    
                    <h5 style={{ color: '#f1f5f9', marginTop: '16px' }}>Game.ini</h5>
                    <CodeBlock code={`[/Script/RCON.RCONServerSystem]
bEnabled=True
ListenPort=7779
Password=xxxxxyz
MaxActiveConnections=2

[/Script/HDMain.HDGameSession]
bSupportersOnlyWhitelist=false`} />

                    <h5 style={{ color: '#f1f5f9', marginTop: '16px' }}>Engine.ini</h5>
                    <CodeBlock code={`[SystemSettings]
Game.FriendlyFireOverride=0
Game.AutoBalanceTeamsOverride=0
HD.AAS.ActiveRouteOverride=0
HD.CP.EnforceSmallerMinPlayersToCapture=-1
HD.CP.MinPlayersToCaptureOverride=-1
HD.Game.MinRespawnDelayOverride=0
HD.Game.DisableKitRestrictionsOverride=0`} />
                  </CardBody>
                </Card>

                <SectionHeader icon={<FaFileCode color="#fbbf24" />} title="Default Factions" />
                <Card style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '20px' }}>
                  <CardBody style={{ padding: '16px', color: '#cbd5e1' }}>
                    <p><b style={{ color: '#fbbf24' }}>Modern</b><br />BP_HDFactionInfo_PMC, BP_HDFactionInfo_Insurgents, BP_HDFactionInfo_PMC_East</p>
                    <p><b style={{ color: '#fbbf24' }}>Gulf War/Cold War</b><br />BP_HDFactionInfo_UnitedStates, BP_HDFactionInfo_UnitedStates_M81, BP_HDFactionInfo_Russians, BP_HDFactionInfo_Russians_VDV</p>
                    <p><b style={{ color: '#fbbf24' }}>Vietnam</b><br />BP_RSFactionInfo_USMC, BP_RSFactionInfo_NVA</p>
                    <p><b style={{ color: '#fbbf24' }}>World War 2</b><br />BP_OOFactionInfo_USA, BP_OOFactionInfo_USRangers, BP_OOFactionInfo_Germany, BP_OOFactionInfo_Wehrmacht</p>
                    <p><b style={{ color: '#fbbf24' }}>World War 1</b><br />BP_OMFactionInfo_UnitedStates, BP_OMFactionInfo_Germany</p>
                    <p><b style={{ color: '#fbbf24' }}>Training Ground</b><br />BP_HDFactionInfo_TG_BluFor, BP_HDFactionInfo_TG_OpFor</p>
                  </CardBody>
                </Card>

                <SectionHeader icon={<FaFileCode color="#fbbf24" />} title="Default Maps" />
                <Card style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '20px' }}>
                  <CardBody style={{ padding: '16px', color: '#cbd5e1' }}>
                    <p><b style={{ color: '#fbbf24' }}>Mode: AdvanceAndSecure</b><br /><code style={{ color: '#60a5fa' }}>?Game=/HDAssets/Core/BP_HDGame_AdvanceAndSecure.BP_HDGame_AdvanceAndSecure_C</code></p>
                    <p>AlDhakir, Argonne, Carentan, Jaziira, Khafji, LamDong, MonteCassino, OmahaBeach, Risala, SaintQuentin, TanBinh</p>

                    <p style={{ marginTop: '16px' }}><b style={{ color: '#fbbf24' }}>Mode: Skirmish</b><br /><code style={{ color: '#60a5fa' }}>?Game=/HDAssets/Core/BP_HDGame_Skirmish.BP_HDGame_Skirmish_C</code></p>
                    <p>AlDhakir_Skirmish_AbandonedApartments, AlDhakir_Skirmish_DesertSettlement, AlDhakir_Skirmish_WadiFarmstead, Argonne_Skirmish_Bunker, Argonne_Skirmish_EastTrenches, Argonne_Skirmish_Ruins, Argonne_Skirmish_WestTrenches, Carentan_Skirmish_Church, Jaziira_Skirmish_City, Khafji_Skirmish_ConstructionSite, Khafji_Skirmish_IndustrialSector, LamDong_Skirmish_Jungle, LamDong_Skirmish_Outpost, LamDong_Skirmish_RiceFields, LamDong_Skirmish_Village, Montecassino_Skirmish_Castle, Montecassino_Skirmish_Villa, OmahaBeach_Skirmish_Beachhead, OmahaBeach_Skirmish_Town, Risala_Skirmish_Highway, Risala_Skirmish_Shipyard, SaintQuentin_Skirmish_NoMansLand, TanBinh_Skirmish_ScorchedFields, TrainingGrouds_Skirmish_Killhouse, TrainingGrouds_Skirmish_UrbanComplex</p>
                  </CardBody>
                </Card>

                <SectionHeader icon={<FaShieldAlt color="#fbbf24" />} title="Systemd Unit File" />
                <Card style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '20px' }}>
                  <CardBody style={{ padding: '16px', color: '#cbd5e1' }}>
                    <p>Path: <code style={{ color: '#fbbf24' }}>/usr/lib/systemd/system/ohdserver.service</code></p>
                    <CodeBlock code={`[Unit]
Description=O:HD Server
After=network.target

[Service]
User=patrick
ExecStart=/home/patrick/OHD_Server_PT_Linux/HarshDoorstopServer.sh
Restart=always

[Install]
WantedBy=multi-user.target`} />
                    <p>Make sure HarshDoorstopServer.sh is executable: <code style={{ color: '#fbbf24' }}>chmod +x HarshDoorstopServer.sh</code></p>
                  </CardBody>
                </Card>

                <SectionHeader icon={<FaLinux color="#fbbf24" />} title="NixOS" />
                <Card style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <CardBody style={{ padding: '16px', color: '#cbd5e1' }}>
                    <p>If you run NixOS to manage your steam server you can use the following shell.nix</p>
                    <p>NixOS shell, path: <code style={{ color: '#fbbf24' }}><a style={{ color: '#60a5fa' }} href="//ohdservers.net/contrib/shell.nix" target="_new">shell.nix</a></code></p>
                    <p>NixOS env config file, path: <code style={{ color: '#fbbf24' }}><a style={{ color: '#60a5fa' }} href="//ohdservers.net/contrib/config.nix" target="_new">.config/nixpkgs/config.nix</a></code></p>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ServerSetup;
